import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

/**
 * Custom wrapper around createAsyncThunk that automatically handles errors with rejectWithValue
 *
 * This eliminates the need for try-catch blocks in every thunk.
 * Just write your async logic - errors are automatically caught and passed to middleware.
 *
 * @example
 * export const fetchData = createAppAsyncThunk(
 *   'slice/fetchData',
 *   async (params) => {
 *     const response = await API_POST(url, params);
 *     return response;
 *   }
 * );
 */
export const createAppAsyncThunk = <Returned, ThunkArg = void>(
  typePrefix: string,
  payloadCreator: (arg: ThunkArg) => Promise<Returned>,
): AsyncThunk<Returned, ThunkArg, Record<string, never>> => {
  return createAsyncThunk(typePrefix, async (arg: ThunkArg, { rejectWithValue }) => {
    try {
      return await payloadCreator(arg);
    } catch (error: unknown) {
      // Serialize error before passing to rejectWithValue to prevent Redux
      // non-serializable value warnings (AxiosError is not serializable).
      // Shape is kept compatible with handleError's isAxiosError type guard
      // (checks isAxiosError === true) so errorMiddleware correctly maps
      // HTTP status / server messages into the global error notification.
      if (axios.isAxiosError(error)) {
        return rejectWithValue({
          isAxiosError: true as const,
          message: error.message,
          response: error.response
            ? { status: error.response.status, data: error.response.data }
            : undefined,
          // Preserve the presence of a request (network error path) without
          // including the full non-serializable Axios request object.
          request: error.request ? {} : undefined,
        });
      }
      if (error instanceof Error) {
        return rejectWithValue({ message: error.message });
      }
      return rejectWithValue({ message: 'An unexpected error occurred' });
    }
  });
};
